import React, { useCallback, useEffect, useState } from "react"
import clsx from "clsx"
import CheckBoxIcon from "@mui/icons-material/CheckBox"
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank"
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

type RadioSelectMultiOption<T> = {
    label: string
    value: T
    disabled?: boolean
}

type RadioSelectMultiProps<T> = {
    options?: RadioSelectMultiOption<T>[]
    value?: T[]
    onChange?: (value: T[]) => void
    required?: boolean
}

export function RadioSelectMulti<T>({
                                        options = [],
                                        value = [],
                                        onChange = () => {},
                                        required = false,
                                    }: RadioSelectMultiProps<T>) {
    // Track selected indices
    const initialSelectedIndices = options
        .map((opt, idx) => (value.includes(opt.value) ? idx : -1))
        .filter(idx => idx >= 0)

    const [selected, setSelected] = useState<number[]>(initialSelectedIndices)

    const isSelected = useCallback(
        (idx: number) => selected.includes(idx),
        [selected]
    )

    const isDisabled = useCallback(
        (idx: number) => options[idx]?.disabled ?? false,
        [options]
    )

    const buttonStyle = useCallback(
        (idx: number) =>
            clsx(
                "px-4 py-2 rounded-md bg-white shadow-md text-left flex gap-4 w-[400px] w-full",
                {
                    "outline outline-light-grey outline-1": !isSelected(idx),
                    "outline outline-blue outline-2": isSelected(idx),
                    "opacity-30": isDisabled(idx),
                }
            ),
        [isSelected, options]
    )

    const handleToggle = useCallback(
        (idx: number) => {
            return () => {
                if (isDisabled(idx)) return

                setSelected(prev => {
                    let updated: number[]
                    if (prev.includes(idx)) {
                        // If required, prevent deselecting last item
                        if (required && prev.length === 1) return prev
                        updated = prev.filter(i => i !== idx)
                    } else {
                        updated = [...prev, idx]
                    }
                    return updated
                })
            }
        },
        [required, options, isDisabled]
    )

    useEffect(() => {
        const values = selected.map(idx => options[idx]?.value).filter(v => v !== undefined) as T[]
        onChange(values)
        // Only re-run when selected changes
    }, [selected, onChange])

    const RadioIcon = useCallback(({index}: { index: number }) => {
        if (isSelected(index)) return <RadioButtonCheckedIcon className={"text-blue"}/>
        return <RadioButtonUncheckedIcon/>
    }, [selected])


    return (
        <div className="flex flex-col gap-4">
            {options.map((option, i) => (
                <div key={i} className="w-full">
                    <button
                        className={buttonStyle(i)}
                        onClick={handleToggle(i)}
                        disabled={isDisabled(i)}
                    >
                        <div className="flex-grow">{option.label}</div>
                        <div>
                            <RadioIcon index={i} />
                        </div>
                    </button>
                </div>
            ))}
        </div>
    )
}
